import { useEffect, useRef, useCallback } from "react"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { $generateHtmlFromNodes } from "@lexical/html"

interface IUpdateStatePlugin {
  onChange: (value: string) => void
  prevData: string
  setPrevData: (prev: string) => void
}

const UpdateStatePlugin = ({ onChange, prevData, setPrevData }: IUpdateStatePlugin) => {
  const [editor] = useLexicalComposerContext()
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Memoize onChange to prevent unnecessary re-registrations
  const onChangeRef = useRef(onChange)
  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  // Debounced onChange handler
  const debouncedOnChange = useCallback((value: string) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
    }

    debounceTimeoutRef.current = setTimeout(() => {
      if (onChangeRef.current) {
        setPrevData(value)
        onChangeRef.current(value)
      }
    }, 100)
  }, [])

  useEffect(() => {
    // Register an update listener to get the latest editor state
    const removeUpdateListener = editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const htmlString = $generateHtmlFromNodes(editor)
        if (prevData !== htmlString) {
          debouncedOnChange(htmlString)
        }
      })
    })

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
      removeUpdateListener() // Clean up the listener
    }
  }, [editor, debouncedOnChange])

  return null
}

export default UpdateStatePlugin
